import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { DashboardResource } from '@/types/planetDashboard';
import { fetchResourceSummary, type ActiveLoanSummary } from '@/api/me';
import { AUTH_TOKEN_KEY } from '@/api/client';
import { useSessionStore } from '@/stores/session.store';

/**
 * Store global de la barre de ressources de l'AppLayout (header).
 *
 * Refonte V4.3 (cf wiki §24.2) : la barre est désormais **per-planète** au lieu
 * d'agréger toutes les planètes du joueur. La planète affichée est :
 *   1. Celle indiquée par `currentPlanetId` (vue qui a un contexte planète actif)
 *   2. À défaut, la planète courante du PlayerUniverse (CurrentPlanetId)
 *   3. À défaut, la 1ère planète possédée triée par Id (= QG)
 *
 * Tant que l'utilisateur est dans le layout `game` (AppLayout monté), le store
 * poll toutes les 15 s pour refléter la production horaire calculée backend.
 *
 * Visibilité dans la barre :
 *   - planetCount === 0  → la barre invite à rejoindre un univers
 *   - planetId !== null  → affiche le nom de la planète + ses stocks
 */
const POLL_INTERVAL_MS = 15_000;

export const useResourceSummaryStore = defineStore('resource-summary', () => {
  const session = useSessionStore();
  // Changement d'univers actif -> refetch immédiat (sinon la barre de niveau/rang
  // garde l'ancien univers jusqu'au prochain poll 15 s).
  watch(() => session.activeUniverseId, () => { void refresh(); });
  const resources = ref<DashboardResource[]>([]);
  const planetCount = ref(0);
  const planetName = ref<string | null>(null);
  const planetId = ref<number | null>(null);
  /** Planète demandée par la vue active (null = laisser le backend choisir). */
  const requestedPlanetId = ref<number | null>(null);
  /**
   * Solde flouz du joueur (PlayerUniverse.Solde). Affiche dans la
   * barre globale et utilise pour les checks UI Shipyard/Research --
   * c'est ce solde qui est debite par les services backend.
   * = PORTEFEUILLE (argent disponible immediatement).
   */
  const playerFlouz = ref(0);
  /**
   * Somme des depots banque sur les planetes possedees. N'est PAS dans
   * le portefeuille -- sert juste a faire des interets. Affiche en
   * info dans le tooltip flouz du header (vue d'ensemble du patrimoine).
   */
  const bankDeposits = ref(0);
  /** Prets banque actifs du joueur (vide si aucun). */
  const activeLoans = ref<ActiveLoanSummary[]>([]);
  /**
   * Emplacements de batiments de la planete affichee (jauge "Places" du
   * header). used = Σ Places non-QG, total = capacite Planet.Places.
   */
  const placesUsed = ref(0);
  const placesTotal = ref(0);
  // -- Progression joueur (barre de niveau du header) : repliée dans le payload
  // /me (cf ResourceSummaryDto) -> une seule source joueur, déjà pollée 15s +
  // rafraîchie sur onTaskResolved. prevScore mémorise le score d'avant le dernier
  // refresh pour surligner le gain récent (segment ambre).
  const level = ref(1);
  const totalScore = ref(0);
  const levelStartScore = ref(0);
  const levelNextScore = ref(0);
  const prevScore = ref(0);
  const levelLoaded = ref(false);
  // -- Rang militaire (barre de combat attachée, dérivée de PointBattle) --
  const combatScore = ref(0);
  const rankOrder = ref(0);
  const rankLabel = ref<string | null>(null);
  const rankImageCode = ref<string | null>(null);
  const rankFloorScore = ref(0);
  const rankNextScore = ref(0);
  const prevCombatScore = ref(0);
  const loading = ref(false);
  let inFlight: Promise<void> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  /** % de remplissage dans le niveau courant (0..1). */
  const levelProgress = computed(() => {
    const span = levelNextScore.value - levelStartScore.value;
    if (span <= 0) return 1;
    return Math.min(1, Math.max(0, (totalScore.value - levelStartScore.value) / span));
  });
  /** % atteint AVANT le dernier gain (borne basse du segment surligné). */
  const levelPrevProgress = computed(() => {
    const span = levelNextScore.value - levelStartScore.value;
    if (span <= 0) return levelProgress.value;
    return Math.min(levelProgress.value, Math.max(0, (prevScore.value - levelStartScore.value) / span));
  });
  /** Gain de score depuis l'avant-dernier refresh (0 si aucun). */
  const recentGain = computed(() => Math.max(0, totalScore.value - prevScore.value));
  /** Tranche de badge (1 par 10 niveaux). */
  const badgeTier = computed(() => Math.max(1, Math.ceil(level.value / 10)));

  /** % de remplissage de la barre de combat (vers le rang suivant ; 1 si rang max). */
  const combatProgress = computed(() => {
    const span = rankNextScore.value - rankFloorScore.value;
    if (span <= 0) return 1;
    return Math.min(1, Math.max(0, (combatScore.value - rankFloorScore.value) / span));
  });
  /** % atteint AVANT le dernier gain de combat (borne basse du segment surligné). */
  const combatPrevProgress = computed(() => {
    const span = rankNextScore.value - rankFloorScore.value;
    if (span <= 0) return combatProgress.value;
    return Math.min(combatProgress.value, Math.max(0, (prevCombatScore.value - rankFloorScore.value) / span));
  });
  /** Gain de combat depuis l'avant-dernier refresh. */
  const recentCombatGain = computed(() => Math.max(0, combatScore.value - prevCombatScore.value));

  /**
   * Setter conservé pour compat : PlanetHomeView pouvait remonter ici les
   * ressources du dashboard d'une seule planète. Aujourd'hui on préfère
   * laisser le polling global gérer la barre — appeler ce setter écrasera
   * volontairement l'agrégat empire jusqu'au prochain refresh.
   */
  function setFromDashboard(payload: {
    planetId: number;
    planetName: string;
    resources: DashboardResource[];
  }): void {
    planetId.value = payload.planetId;
    planetName.value = payload.planetName;
    resources.value = payload.resources;
    planetCount.value = 1;
  }

  function clear(): void {
    stop();
    resources.value = [];
    planetCount.value = 0;
    planetName.value = null;
    planetId.value = null;
    requestedPlanetId.value = null;
    playerFlouz.value = 0;
    bankDeposits.value = 0;
    activeLoans.value = [];
    placesUsed.value = 0;
    placesTotal.value = 0;
    level.value = 1;
    totalScore.value = 0;
    levelStartScore.value = 0;
    levelNextScore.value = 0;
    prevScore.value = 0;
    levelLoaded.value = false;
    combatScore.value = 0;
    rankOrder.value = 0;
    rankLabel.value = null;
    rankImageCode.value = null;
    rankFloorScore.value = 0;
    rankNextScore.value = 0;
    prevCombatScore.value = 0;
  }

  /**
   * Indique la planète à afficher dans la barre header. Appelé par les vues
   * qui ont un contexte planète actif (PlanetHomeView, BuildingManagement…).
   * Si la valeur change, on déclenche un refresh immédiat pour que la barre
   * suive sans attendre le prochain poll.
   */
  function setCurrentPlanet(id: number | null): void {
    if (requestedPlanetId.value === id) return;
    requestedPlanetId.value = id;
    void refresh();
  }

  // Surlignage du dernier gain : on garde le segment visible ~8 s après un gain,
  // même si un refresh SANS gain survient entre-temps (sinon le poll 15 s
  // remettait prevScore = total -> surlignage effacé quasi immédiatement).
  const GAIN_HIGHLIGHT_MS = 8000;
  let lvlGainTimer: ReturnType<typeof setTimeout> | null = null;
  let cbtGainTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Refetch immédiat — silencieux en cas d'échec (pas de partie active,
   * token expiré, endpoint indispo). Reentrant safe via `inFlight`.
   */
  async function refresh(): Promise<void> {
    if (inFlight) return inFlight;
    // Pas de token = pas authentifié, inutile d'interroger l'API.
    if (!localStorage.getItem(AUTH_TOKEN_KEY)) return;
    loading.value = true;
    inFlight = (async () => {
      try {
        // Scoper /me à l'univers ACTIVEMENT sélectionné (et non « la partie la plus
        // récente par LastLoginAt ») : sinon la barre de niveau/rang peut refléter un
        // AUTRE univers que celui joué (coffres/planètes scopés activePlayerUniverseId)
        // -> impression de niveaux/coffres qui « transitent » entre univers.
        const summary = await fetchResourceSummary(
          session.activeUniverseId ?? undefined,
          requestedPlanetId.value ?? undefined,
        );
        planetCount.value = summary.planetCount;
        playerFlouz.value = summary.playerFlouz ?? 0;
        bankDeposits.value = summary.bankDeposits ?? 0;
        activeLoans.value = summary.activeLoans ?? [];
        placesUsed.value = summary.placesUsed ?? 0;
        placesTotal.value = summary.placesTotal ?? 0;
        // Progression : on n'avance la borne basse du surlignage QUE sur un vrai
        // gain (puis on la remet au niveau courant après GAIN_HIGHLIGHT_MS). Sans
        // gain, on ne touche pas prevScore -> le segment du dernier gain persiste.
        const newTotal = summary.totalScore ?? 0;
        const newCombat = summary.combatScore ?? 0;
        if (!levelLoaded.value) {
          prevScore.value = newTotal;
          prevCombatScore.value = newCombat;
        } else {
          if (newTotal > totalScore.value) {
            prevScore.value = totalScore.value;
            if (lvlGainTimer) clearTimeout(lvlGainTimer);
            lvlGainTimer = setTimeout(() => { prevScore.value = totalScore.value; }, GAIN_HIGHLIGHT_MS);
          }
          if (newCombat > combatScore.value) {
            prevCombatScore.value = combatScore.value;
            if (cbtGainTimer) clearTimeout(cbtGainTimer);
            cbtGainTimer = setTimeout(() => { prevCombatScore.value = combatScore.value; }, GAIN_HIGHLIGHT_MS);
          }
        }
        level.value = summary.level ?? 1;
        totalScore.value = newTotal;
        levelStartScore.value = summary.levelStartScore ?? 0;
        levelNextScore.value = summary.levelNextScore ?? 0;
        combatScore.value = summary.combatScore ?? 0;
        rankOrder.value = summary.rankOrder ?? 0;
        rankLabel.value = summary.rankLabel ?? null;
        rankImageCode.value = summary.rankImageCode ?? null;
        rankFloorScore.value = summary.rankFloorScore ?? 0;
        rankNextScore.value = summary.rankNextScore ?? 0;
        levelLoaded.value = true;
        if (summary.planetCount === 0) {
          resources.value = [];
          planetName.value = null;
          planetId.value = null;
          return;
        }
        resources.value = summary.resources.map(r => ({
          resourceId: r.resourceId,
          code: r.code,
          label: r.label,
          available: r.available,
          stored: r.stored ?? 0,
          productionPerHour: r.productionPerHour,
          category: r.category ?? 'Consommable',
          origin: r.origin,
          suggestedUnitPrice: r.suggestedUnitPrice,
          // Champs Population (renseignés uniquement sur l'entrée habitants).
          woundedPopulation: r.woundedPopulation,
          laborUsed: r.laborUsed,
          laborAvailable: r.laborAvailable,
          laborRatioPct: r.laborRatioPct,
        }));
        planetId.value = summary.planetId;
        planetName.value = summary.planetName;
      } catch {
        // Silencieux : on conserve les valeurs précédentes si l'API échoue.
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  /**
   * Démarre le polling périodique. Idempotent — pas de doublons si déjà
   * actif. À appeler depuis le AppLayout (onMounted) pour que la barre
   * vive aussi longtemps que le joueur est dans le layout game.
   */
  function start(): void {
    void refresh();
    if (pollTimer) return;
    pollTimer = setInterval(() => { void refresh(); }, POLL_INTERVAL_MS);
  }

  function stop(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  return {
    resources, planetCount, planetName, planetId, playerFlouz, loading,
    bankDeposits, activeLoans, placesUsed, placesTotal,
    requestedPlanetId,
    // Progression (barre de niveau)
    level, totalScore, levelStartScore, levelNextScore, prevScore,
    levelProgress, levelPrevProgress, recentGain, badgeTier,
    // Rang militaire (barre de combat)
    combatScore, rankOrder, rankLabel, rankImageCode, rankFloorScore, rankNextScore,
    combatProgress, combatPrevProgress, recentCombatGain,
    setFromDashboard, setCurrentPlanet, clear, refresh, start, stop,
  };
});
